import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router";
import { Copy, Loader2, Plus, Search } from "lucide-react";
import { v4 } from "uuid";
import t from "@/lang/lang";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { useGfsCalculationListQuery } from "@/services/backend/gfs/calculation/api";
import { GfsCalculationListItem } from "@/services/backend/gfs/calculation/types";
import { useGfsCatalogListQuery } from "@/services/backend/gfs/catalog/api";
import { preferredGfsCatalogId } from "@/services/backend/gfs/catalog/catalog-selection";
import {
  useGfsOfferCreateFromCalculationMutation,
  useGfsOfferCreateFromOldMutation,
  useGfsOfferCreateMutation,
  useGfsOfferListQuery,
} from "@/services/backend/gfs/offer/api";
import { GfsOfferListItem } from "@/services/backend/gfs/offer/types";
import { Button } from "@/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  MilestonePage,
  MilestonePageActions,
  MilestonePageContent,
  MilestonePageHeader,
  MilestonePageTitle,
} from "@/shared/bsm/core/milestone/components/ui/milestone-page";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { formatDate, formatTime } from "@/shared/lib/utilities/date";
import { toast } from "sonner";

export function GfsOfferListPage() {
  const { processId = "" } = useParams();
  const { data: process, error: processError } = useProcessesProcessShowQuery({
    id: processId,
  });

  if (processError) return <RTKQueryErrorAlert error={processError} />;
  if (!process) return null;
  if (!process.customerId) {
    return <div>{t("Diesem Vorgang ist noch kein Kunde zugeordnet.")}</div>;
  }

  return (
    <OfferPage companyId={process.companyId} customerId={process.customerId} />
  );
}

function OfferPage({
  companyId,
  customerId,
}: {
  companyId: string;
  customerId: string;
}) {
  const {
    data: offers,
    error: offerError,
    isLoading: offersLoading,
  } = useGfsOfferListQuery({
    company: { active: true, values: [companyId] },
    customer: { active: true, values: [customerId] },
  });
  const {
    data: calculations,
    error: calculationError,
    isLoading: calculationsLoading,
  } = useGfsCalculationListQuery({
    company: { active: true, values: [companyId] },
    customer: { active: true, values: [customerId] },
  });

  return (
    <MilestonePage>
      <MilestonePageHeader>
        <MilestonePageTitle>{t("Angebote")}</MilestonePageTitle>
        <MilestonePageActions>
          <CreateOfferDialog
            calculations={calculations?.items ?? []}
            calculationsLoading={calculationsLoading}
            companyId={companyId}
            customerId={customerId}
            offers={offers?.items ?? []}
          />
        </MilestonePageActions>
      </MilestonePageHeader>
      <MilestonePageContent>
        {offerError && <RTKQueryErrorAlert error={offerError} />}
        {calculationError && <RTKQueryErrorAlert error={calculationError} />}
        <Card>
          <OfferHistoryTable
            isLoading={offersLoading}
            items={offers?.items ?? []}
          />
        </Card>
      </MilestonePageContent>
    </MilestonePage>
  );
}

function CreateOfferDialog({
  calculations,
  calculationsLoading,
  companyId,
  customerId,
  offers,
}: {
  calculations: GfsCalculationListItem[];
  calculationsLoading: boolean;
  companyId: string;
  customerId: string;
  offers: GfsOfferListItem[];
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const closeDialogAndNavigate = useCallback(
    (offerId: string) => {
      setOpen(false);
      navigate(offerId);
    },
    [navigate],
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-5 w-5" />
          <span>{t("Angebot")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[80vh] min-w-[80vw] overflow-y-scroll">
        <DialogHeader>
          <DialogTitle>{t("Neues Angebot erstellen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Angebote können komplett neu, auf Basis einer Kalkulation oder auf Basis eines alten Angebots erstellt werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="manual">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="manual">{t("Neu Anlegen")}</TabsTrigger>
            <TabsTrigger value="from-calculation">
              {t("Kalkulationen")}
            </TabsTrigger>
            <TabsTrigger value="from-old">{t("Alte Angebote")}</TabsTrigger>
          </TabsList>
          <TabsContent value="manual">
            <CreateManually
              companyId={companyId}
              customerId={customerId}
              onCreated={closeDialogAndNavigate}
            />
          </TabsContent>
          <TabsContent value="from-calculation">
            <CreateFromCalculation
              calculations={calculations}
              calculationsLoading={calculationsLoading}
              companyId={companyId}
              customerId={customerId}
              onCreated={closeDialogAndNavigate}
            />
          </TabsContent>
          <TabsContent value="from-old">
            <CreateFromOld
              companyId={companyId}
              customerId={customerId}
              offers={offers}
              onCreated={closeDialogAndNavigate}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function CreateManually({
  companyId,
  customerId,
  onCreated,
}: {
  companyId: string;
  customerId: string;
  onCreated: (offerId: string) => void;
}) {
  const [create, { isLoading, error }] = useGfsOfferCreateMutation();
  const { data: catalogs, error: catalogError } = useGfsCatalogListQuery({
    company: { active: true, values: [companyId] },
    limit: 100,
  });
  const [catalogId, setCatalogId] = useState("");
  const availableCatalogs = (catalogs?.items ?? []).filter(
    (catalog) => !catalog.customerId || catalog.customerId === customerId,
  );

  useEffect(() => {
    if (catalogId) return;
    const preferredCatalogId = preferredGfsCatalogId(
      availableCatalogs,
      customerId,
    );
    if (preferredCatalogId) setCatalogId(preferredCatalogId);
  }, [availableCatalogs, catalogId, customerId]);

  const onClick = () => {
    if (isLoading) return;
    const id = v4();
    create({ id, companyId, customerId, catalogId })
      .unwrap()
      .then(() => onCreated(id));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Neu Anlegen")}</CardTitle>
        <CardDescription>
          {t(
            "Erstellt ein leeres Angebot mit den Basisdaten des Kunden. Die Kalkulation kann anschließend gewählt werden.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2">
          <label className="text-sm font-medium">{t("Katalog")}</label>
          <Select value={catalogId} onValueChange={setCatalogId}>
            <SelectTrigger>
              <SelectValue placeholder={t("Katalog auswählen")} />
            </SelectTrigger>
            <SelectContent>
              {availableCatalogs.map((catalog) => (
                <SelectItem key={catalog.id} value={catalog.id}>
                  {catalog.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <RTKQueryErrorAlert error={catalogError} />
        <RTKQueryErrorAlert error={error} />
      </CardContent>
      <CardFooter className="justify-end">
        <Button onClick={onClick} disabled={isLoading || !catalogId}>
          {t("Erstellen")}
        </Button>
      </CardFooter>
    </Card>
  );
}

function CreateFromCalculation({
  calculations,
  calculationsLoading,
  companyId,
  customerId,
  onCreated,
}: {
  calculations: GfsCalculationListItem[];
  calculationsLoading: boolean;
  companyId: string;
  customerId: string;
  onCreated: (offerId: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Daten auf Basis einer Kalkulation laden")}</CardTitle>
        <CardDescription>
          {t("Erstellt ein Angebot und kopiert die gewählte Kalkulation.")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <CalculationsTable
          calculations={calculations}
          calculationsLoading={calculationsLoading}
          companyId={companyId}
          customerId={customerId}
          onCreated={onCreated}
        />
      </CardContent>
    </Card>
  );
}

function CalculationsTable({
  calculations,
  calculationsLoading,
  companyId,
  customerId,
  onCreated,
}: {
  calculations: GfsCalculationListItem[];
  calculationsLoading: boolean;
  companyId: string;
  customerId: string;
  onCreated: (offerId: string) => void;
}) {
  const sortedItems = useMemo(
    () => [...calculations].sort(byCreatedAtAndId),
    [calculations],
  );

  if (calculationsLoading) {
    return (
      <Table className="animate-pulse">
        <TableHeader>
          <TableRow>
            <TableHead>{t("Nummer")}</TableHead>
            <TableHead>{t("Erstellt am")}</TableHead>
            <TableHead>{t("Aktualisiert am")}</TableHead>
            <TableHead className="w-full">{t("Bestandsaufnahme")}</TableHead>
            <TableHead className="text-right">{t("Summe")}</TableHead>
            <TableHead>{t("Aktionen")}</TableHead>
          </TableRow>
        </TableHeader>
      </Table>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead>{t("Erstellt am")}</TableHead>
          <TableHead>{t("Aktualisiert am")}</TableHead>
          <TableHead className="w-full">{t("Bestandsaufnahme")}</TableHead>
          <TableHead className="text-right">{t("Summe")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedItems.map((calculation) => (
          <TableRow key={calculation.id}>
            <TableCell>
              <DocumentNumberById
                documentId={calculation.id}
                skip={!calculation.immutable}
              />
            </TableCell>
            <TableCell>{formatDate(calculation.createdAt)}</TableCell>
            <TableCell>{formatDate(calculation.updatedAt)}</TableCell>
            <TableCell>
              {calculation.assessmentSubject || t("Nicht gewählt")}
            </TableCell>
            <TableCell className="text-right">
              {formatCurrency(calculation.pricingTotal)}
            </TableCell>
            <TableCell>
              <CreateOfferFromCalculationButton
                calculationId={calculation.id}
                companyId={companyId}
                customerId={customerId}
                onCreated={onCreated}
              />
            </TableCell>
          </TableRow>
        ))}
        {sortedItems.length === 0 && (
          <TableRow>
            <TableCell colSpan={6} className="text-center">
              {t("Keine Kalkulationen gefunden")}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function CreateOfferFromCalculationButton({
  calculationId,
  companyId,
  customerId,
  onCreated,
}: {
  calculationId: string;
  companyId: string;
  customerId: string;
  onCreated: (offerId: string) => void;
}) {
  const [create, { isLoading, error }] =
    useGfsOfferCreateFromCalculationMutation();

  const onClick = () => {
    if (isLoading) return;
    const id = v4();
    create({ id, companyId, customerId, calculationId })
      .unwrap()
      .then(() => onCreated(id))
      .catch(() => {
        toast.error(t("Fehler"), {
          description: t("Das Angebot konnte nicht erstellt werden."),
        });
      });
  };

  return (
    <div className="flex items-center gap-2">
      {error && <RTKQueryErrorAlert error={error} />}
      <Button onClick={onClick} disabled={isLoading} size="sm">
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        {!isLoading && (
          <>
            <Copy className="mr-3 h-4 w-4" />
            <span>{t("Daten Übernehmen")}</span>
          </>
        )}
      </Button>
    </div>
  );
}

function CreateFromOld({
  companyId,
  customerId,
  offers,
  onCreated,
}: {
  companyId: string;
  customerId: string;
  offers: GfsOfferListItem[];
  onCreated: (offerId: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Auf Basis alter Angebote")}</CardTitle>
        <CardDescription>
          {t(
            "Kopiert die Daten eines bestehenden Angebots. Es werden Angebote dieses Kunden angezeigt.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <OldOffersTable
          companyId={companyId}
          customerId={customerId}
          offers={offers}
          onCreated={onCreated}
        />
      </CardContent>
    </Card>
  );
}

function OldOffersTable({
  companyId,
  customerId,
  offers,
  onCreated,
}: {
  companyId: string;
  customerId: string;
  offers: GfsOfferListItem[];
  onCreated: (offerId: string) => void;
}) {
  const sortedItems = useMemo(
    () => [...offers].sort(byCreatedAtAndId),
    [offers],
  );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Datum")}</TableHead>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead className="w-full">{t("Thema")}</TableHead>
          <TableHead className="text-right">{t("Summe")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedItems.map((offer) => (
          <TableRow key={offer.id}>
            <TableCell>{formatDate(offer.createdAt)}</TableCell>
            <TableCell>
              <DocumentNumberById
                documentId={offer.id}
                skip={!offer.immutable}
              />
            </TableCell>
            <TableCell>{offer.subject || t("Ohne Betreff")}</TableCell>
            <TableCell className="text-right">
              {formatCurrency(offer.pricingTotal)}
            </TableCell>
            <TableCell>
              <div className="flex space-x-2">
                <NavLink to={offer.id}>
                  <Button variant="outline" size="sm">
                    <Search className="h-4 w-4" />
                  </Button>
                </NavLink>
                <CopyOfferButton
                  companyId={companyId}
                  customerId={customerId}
                  offerId={offer.id}
                  onCopied={onCreated}
                />
              </div>
            </TableCell>
          </TableRow>
        ))}
        {sortedItems.length === 0 && (
          <TableRow>
            <TableCell colSpan={5} className="text-center">
              {t("Keine Angebote gefunden")}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function CopyOfferButton({
  companyId,
  customerId,
  offerId,
  onCopied,
}: {
  companyId: string;
  customerId: string;
  offerId: string;
  onCopied: (offerId: string) => void;
}) {
  const [copy, { isLoading, error }] = useGfsOfferCreateFromOldMutation();

  const onClick = () => {
    if (isLoading) return;
    const newOfferId = v4();
    copy({
      oldOfferId: offerId,
      newOfferId,
      companyId,
      customerId,
    })
      .unwrap()
      .then(() => onCopied(newOfferId))
      .catch(() => {
        toast.error(t("Fehler"), {
          description: t("Das Angebot konnte nicht kopiert werden."),
        });
      });
  };

  return (
    <div className="flex items-center gap-2">
      {error && <RTKQueryErrorAlert error={error} />}
      <Button onClick={onClick} disabled={isLoading} size="sm">
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        {!isLoading && (
          <>
            <Copy className="mr-3 h-4 w-4" />
            <span>{t("Kopieren")}</span>
          </>
        )}
      </Button>
    </div>
  );
}

function OfferHistoryTable({
  isLoading,
  items,
}: {
  isLoading: boolean;
  items: GfsOfferListItem[];
}) {
  if (isLoading) {
    return <OfferHistoryTableSkeleton />;
  }

  const sortedItems = [...items].sort(byCreatedAtAndId);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Datum")}</TableHead>
          <TableHead>{t("Uhrzeit")}</TableHead>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead className="w-full">{t("Thema")}</TableHead>
          <TableHead className="text-right">{t("Summe")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedItems.map((offer) => (
          <TableRow key={offer.id}>
            <TableCell>{formatDate(offer.createdAt)}</TableCell>
            <TableCell>{formatTime(offer.createdAt)}</TableCell>
            <TableCell>
              <NavLink to={`${offer.id}`}>
                <DocumentNumberById
                  documentId={offer.id}
                  skip={!offer.immutable}
                />
              </NavLink>
            </TableCell>
            <TableCell>{offer.subject || t("Ohne Betreff")}</TableCell>
            <TableCell className="text-right">
              {formatCurrency(offer.pricingTotal)}
            </TableCell>
            <TableCell>
              <NavLink to={`${offer.id}`}>
                <Button variant="ghost">
                  <Search className="h-4 w-4" />
                </Button>
              </NavLink>
            </TableCell>
          </TableRow>
        ))}
        {items.length === 0 && (
          <TableRow>
            <TableCell colSpan={6} className="text-center">
              {t("Keine Angebote gefunden")}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function OfferHistoryTableSkeleton() {
  return (
    <Table className="animate-pulse">
      <TableHeader>
        <TableRow>
          <TableHead>{t("Datum")}</TableHead>
          <TableHead>{t("Uhrzeit")}</TableHead>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead className="w-full">{t("Thema")}</TableHead>
          <TableHead className="text-right">{t("Summe")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
    </Table>
  );
}

function byCreatedAtAndId(
  left: { createdAt: string; id: string },
  right: { createdAt: string; id: string },
) {
  const createdAtOrder = right.createdAt.localeCompare(left.createdAt);
  if (createdAtOrder !== 0) return createdAtOrder;
  return right.id.localeCompare(left.id);
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(value ?? 0);
}
